/**
 * CoverageChatDrawer — AI assistant chat interface for officers.
 * Sprint 16: FR-COV-CHAT
 *
 * Slide-in drawer accessible from CoverageReviewPanel.
 * Maintains conversation history client-side, sends to stateless AI endpoint.
 */

import { useState, useRef, useEffect } from 'react';
import { useCoverage } from '../hooks/useCoverage';

interface CoverageChatDrawerProps {
  requestId: number;
  open: boolean;
  onClose: () => void;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const SUGGESTED_PROMPTS = [
  'Who needs hours most?',
  'Who worked recently?',
  'Coverage history for this battalion?',
  'Why is the top pick ranked first?',
];

export default function CoverageChatDrawer({ requestId, open, onClose }: CoverageChatDrawerProps) {
  const { sendChatMessage } = useCoverage();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (text?: string) => {
    const message = text || input.trim();
    if (!message || loading) return;

    setInput('');
    const userMsg: ChatMessage = { role: 'user', content: message };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await sendChatMessage(requestId, message, messages);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.response }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', content: `Error: ${(err as Error).message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setMessages([]);
    setInput('');
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        maxWidth: 420,
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#0a1220',
        borderLeft: '1px solid rgba(212, 168, 67, 0.2)',
        boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.4)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ margin: 0, color: '#D4A843', fontSize: 16, fontWeight: 700 }}>AI Assistant</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                padding: '4px 10px',
                fontSize: 12,
                backgroundColor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 4,
                color: '#888',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '4px 10px',
              fontSize: 14,
              backgroundColor: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {messages.length === 0 && (
          <div style={{ color: '#666', fontSize: 14, textAlign: 'center', marginTop: 24 }}>
            Ask me about the candidates, their hours, or coverage history.
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              padding: '10px 14px',
              borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
              backgroundColor: msg.role === 'user' ? 'rgba(212, 168, 67, 0.15)' : 'rgba(255, 255, 255, 0.06)',
              color: msg.role === 'user' ? '#D4A843' : '#ddd',
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {msg.content}
          </div>
        ))}

        {loading && (
          <div
            style={{
              alignSelf: 'flex-start',
              padding: '10px 14px',
              borderRadius: '12px 12px 12px 4px',
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              color: '#888',
              fontSize: 14,
            }}
          >
            Thinking...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested prompts */}
      {messages.length === 0 && (
        <div
          style={{
            padding: '0 20px 12px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          {SUGGESTED_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => handleSend(prompt)}
              style={{
                padding: '6px 12px',
                fontSize: 12,
                borderRadius: 16,
                border: '1px solid rgba(212, 168, 67, 0.25)',
                backgroundColor: 'transparent',
                color: '#D4A843',
                cursor: 'pointer',
              }}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        style={{
          padding: '12px 20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          gap: 8,
        }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about candidates..."
          disabled={loading}
          style={{
            flex: 1,
            padding: '10px 14px',
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: 8,
            color: '#fff',
            fontSize: 14,
            outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          style={{
            padding: '10px 16px',
            backgroundColor: '#D4A843',
            border: 'none',
            borderRadius: 8,
            color: '#0F1B2D',
            fontSize: 14,
            fontWeight: 700,
            cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
            opacity: loading || !input.trim() ? 0.5 : 1,
          }}
        >
          Send
        </button>
      </form>
    </div>
  );
}
